/**
 * StatusBadge — legacy variant-name API delegating onto the canonical
 * six-state StateChip (K3 Black + Orange law).
 *
 * Design law: docs/plans/2026-07-24-k3-black-orange-design-law.md §2
 *
 * The old implementation collapsed ~16 variant names to 4 styles and mapped
 * error/blocked/contested to the SAME orange as pending — states with legal
 * consequence were indistinguishable from "still running". Every variant now
 * routes to one of six states with distinct hue/fill/icon/weight:
 *
 *   complete / success / done / supported        → verified      (hueless ✓, quietest)
 *   error / blocked / contested                  → blocker       (red, octagon-X)
 *   pending / attention / in_progress /
 *     conditional                                → needs-human   (orange tint, user-check)
 *   warning / stale                              → warning       (orange outline, triangle)
 *   active                                       → system-active (orange tint, loader)
 *   not_started / waiting / queued / neutral     → dormant       (ghost, hollow circle)
 *   anything unmapped                            → dormant + one console.warn
 *
 * External API (variant names, size prop) is 100% unchanged — no call site breaks.
 */

import React from 'react';
import { StateChip, ChipState } from './StateChip';

type BadgeVariant =
  | 'complete' | 'active' | 'success' | 'done'
  | 'pending' | 'warning' | 'in_progress' | 'attention'
  | 'blocked' | 'error' | 'contested'
  | 'neutral' | 'not_started' | 'waiting' | 'conditional';

interface StatusBadgeProps {
  text: string;
  variant: BadgeVariant;
  className?: string;
  size?: 'sm' | 'md';
}

/**
 * Keyed as Record<string, ...> on purpose: real call sites pass variant names
 * outside the declared union (e.g. 'stale' / 'supported' in
 * components/EngineAuditPanel.tsx via untyped maps). Those are mapped
 * explicitly; genuinely unknown names degrade to dormant with a single warn.
 */
const VARIANT_TO_STATE: Record<string, ChipState> = {
  // Verified — engine-sourced, resolved. Hueless by design.
  complete: 'verified',
  success: 'verified',
  done: 'verified',
  supported: 'verified',

  // Blocker — legal consequence. Red, never orange.
  error: 'blocker',
  blocked: 'blocker',
  contested: 'blocker',

  // Needs human — a person must look / confirm.
  pending: 'needs-human',
  attention: 'needs-human',
  in_progress: 'needs-human',
  conditional: 'needs-human',

  // Warning — orange outline; icon + fill distinguish it from needs-human.
  warning: 'warning',
  stale: 'warning',

  // System active — machine running, not a human task.
  active: 'system-active',

  // Dormant — not run / queued / no signal.
  not_started: 'dormant',
  waiting: 'dormant',
  queued: 'dormant',
  neutral: 'dormant',
};

const warnedVariants = new Set<string>();

export function StatusBadge({ text, variant, className, size = 'sm' }: StatusBadgeProps) {
  let state = VARIANT_TO_STATE[variant];
  if (!state) {
    if (!warnedVariants.has(variant)) {
      warnedVariants.add(variant);
      // eslint-disable-next-line no-console
      console.warn(
        `StatusBadge: unmapped variant "${variant}" — rendering as dormant. ` +
          'Add it to VARIANT_TO_STATE in StatusBadge.tsx.'
      );
    }
    state = 'dormant';
  }
  return <StateChip state={state} text={text} size={size} className={className} />;
}

export default StatusBadge;
